/**
 * Procedurally rendered impulse responses. Reverb is what turns a stack of
 * oscillators into a place: the tail tells the player how big the room is and
 * what it is made of, long before they can see either.
 *
 * Each response is decaying noise, low-passed by a one-pole filter that closes
 * as the tail ages (high frequencies die first in a real room), with a handful
 * of discrete early reflections in front of it. The early taps carry most of the
 * perceived size; the tail carries the material.
 */

import { makeRNG } from '../core/rng';
import type { AmbienceProfile } from '../core/types';

export interface ReverbSpec {
  /** Tail length in seconds. */
  seconds: number;
  /** Decay curve exponent. Higher collapses faster. */
  decay: number;
  /** 0..1 of the top end lost across the length of the tail. */
  damping: number;
  /** Gap before the tail arrives. Reads as distance to the nearest surface. */
  predelay: number;
  /** 0..1 initial one-pole coefficient. Low is dull plaster, high is tile. */
  brightness: number;
  /** Early reflections as [seconds, amplitude]. */
  taps: ReadonlyArray<readonly [number, number]>;
  /** How much of this space the return bus should carry. */
  wet: number;
}

/**
 * One space per ambience profile. Level 0's lobby is deliberately dead — thick
 * carpet and low ceilings eat the tail — while the poolrooms ring for seconds.
 */
export const REVERBS: Record<AmbienceProfile, ReverbSpec> = {
  lobby: {
    seconds: 2.4,
    decay: 2.7,
    damping: 0.86,
    predelay: 0.011,
    brightness: 0.2,
    taps: [
      [0.013, 0.34],
      [0.024, 0.23],
      [0.038, 0.16],
      [0.055, 0.1],
    ],
    wet: 0.34,
  },
  warehouse: {
    seconds: 3.8,
    decay: 2.1,
    damping: 0.78,
    predelay: 0.024,
    brightness: 0.15,
    taps: [
      [0.028, 0.3],
      [0.049, 0.22],
      [0.081, 0.16],
      [0.118, 0.11],
      [0.163, 0.07],
    ],
    wet: 0.5,
  },
  pipes: {
    // A tight slap: nothing here is more than a couple of metres away.
    seconds: 0.55,
    decay: 3.4,
    damping: 0.5,
    predelay: 0.004,
    brightness: 0.52,
    taps: [
      [0.005, 0.5],
      [0.0102, 0.36],
      [0.0171, 0.24],
      [0.0263, 0.15],
    ],
    wet: 0.48,
  },
  poolrooms: {
    seconds: 4.8,
    decay: 1.6,
    damping: 0.32,
    predelay: 0.017,
    brightness: 0.64,
    taps: [
      [0.019, 0.32],
      [0.033, 0.26],
      [0.052, 0.2],
      [0.079, 0.15],
      [0.112, 0.1],
    ],
    wet: 0.8,
  },
  menu: {
    seconds: 3.2,
    decay: 2.0,
    damping: 0.72,
    predelay: 0.02,
    brightness: 0.28,
    taps: [
      [0.021, 0.26],
      [0.04, 0.18],
      [0.067, 0.12],
    ],
    wet: 0.38,
  },
  silence: {
    seconds: 1.8,
    decay: 3.0,
    damping: 0.9,
    predelay: 0.009,
    brightness: 0.18,
    taps: [
      [0.011, 0.28],
      [0.02, 0.18],
    ],
    wet: 0.26,
  },
};

/** Fixed seed so a given space sounds identical in every session. */
const IR_SEED = 0x1a7e;

/**
 * Render `spec` to a stereo impulse response. Costs a few milliseconds and is
 * only ever done once per space, on the level load that first needs it.
 */
export function renderImpulse(ctx: BaseAudioContext, spec: ReverbSpec, salt: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const len = Math.max(2, Math.floor(sr * spec.seconds));
  const buf = ctx.createBuffer(2, len, sr);
  const rng = makeRNG(IR_SEED ^ salt);
  const pre = Math.min(len - 1, Math.floor(spec.predelay * sr));
  const span = len - pre;

  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = pre; i < len; i++) {
      const t = (i - pre) / span;
      // Coefficient falls with age, so the tail darkens as it dies away.
      const a = Math.max(0.015, spec.brightness * (1 - spec.damping * t));
      lp += ((rng.next() * 2 - 1) - lp) * a;
      d[i] = lp * Math.pow(1 - t, spec.decay);
    }
    // Offset the right channel's taps slightly: identical early reflections in
    // both ears collapse the image to the centre.
    const skew = c === 1 ? 0.0029 : 0;
    for (const [time, g] of spec.taps) {
      const idx = pre + Math.floor((time + skew) * sr);
      if (idx < len) d[idx]! += g * (rng.next() < 0.5 ? -1 : 1);
    }
  }

  normalizePeak(buf, 0.9);
  return buf;
}

/**
 * Bring the response into a sane range. The convolver runs with `normalize`
 * left on, which applies the spec's RMS calibration — that is what keeps a
 * 0.55s slap and a 4.8s hall at comparable perceived levels, so the `wet`
 * numbers above mean the same thing in every space. All this has to do is not
 * hand it something degenerate.
 */
function normalizePeak(buf: AudioBuffer, target: number): void {
  let peak = 0;
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < d.length; i++) {
      const a = Math.abs(d[i]!);
      if (a > peak) peak = a;
    }
  }
  if (peak < 1e-6) return;
  const scale = target / peak;
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < d.length; i++) d[i]! *= scale;
  }
}
